import { Link, useParams, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Seo from '@/components/base/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList, categories, getCategory, siteInfo } from '@/mocks/anime';

export default function Category() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const current = id ? getCategory(id) : undefined;

  const kw = q.trim().toLowerCase();
  const filtered = animeList.filter((a) => {
    const matchCat = !id || a.category === id;
    const matchKw = !kw || a.title.toLowerCase().includes(kw) || a.tags.some((t) => t.toLowerCase().includes(kw));
    return matchCat && matchKw;
  });

  const title = current ? `${current.name} - 动漫网分类导航` : '动漫分类 - 动漫网导航页';
  const description = current
    ? `${current.name}是动漫网热门分类之一，共收录 ${current.count} 部佳作。动漫网官网首页直达，分类直达链接清晰。`
    : '动漫网分类导航页，覆盖热血冒险、校园恋爱、奇幻异界、科幻机甲、悬疑惊悚、治愈日常六大分类，动漫网官网首页直达，想看番直接进。';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      url: `${siteInfo.url}/category${id ? `/${id}` : ''}`,
      description,
      inLanguage: 'zh-CN',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: filtered.map((a, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: a.title,
          url: `${siteInfo.url}/category/${a.category}`,
        })),
      },
    },
  ];

  return (
    <Layout>
      <Seo title={title} description={description} path={`/category${id ? `/${id}` : ''}`} jsonLd={jsonLd} />

      <div className="bg-gradient-to-br from-primary-100 via-background-100 to-accent-100 pt-24 pb-8">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '动漫分类', path: '/category' },
              ...(current ? [{ name: current.name }] : []),
            ]}
          />
          <h1 className="mt-3 font-heading text-3xl text-foreground-950">
            {current ? `${current.name}动漫` : '动漫分类导航'}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            {current ? current.desc : '六大热门分类一网打尽，动漫网网址收藏、直达链接、主页导航全都有。'}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 md:px-6 py-6">
        <nav aria-label="分类切换" className="flex flex-wrap items-center gap-2">
          <Link
            to="/category"
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              !id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                id === c.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </nav>

        {q && (
          <p className="mt-4 text-sm text-foreground-600">
            搜索“<span className="font-medium text-primary-600">{q}</span>”共找到 {filtered.length} 部作品
          </p>
        )}

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((item) => (
              <AnimeCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300" aria-hidden="true" />
            <p className="mt-4 text-foreground-600">没有找到匹配的作品，试试其他关键词或分类吧。</p>
            <Link to="/category" className="mt-4 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors">
              返回全部分类
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}